import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { UploadCloud } from "lucide-react";
import { toast } from "sonner";

import { SectionHeading } from "@/components/section-heading";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { listMyProfileImages, submitProfileImage } from "@/lib/account.functions";
import { AgeVerificationPanel } from "@/components/age-verification-panel";

export const Route = createFileRoute("/_authenticated/settings/profile-images")({
  head: () => ({
    meta: [
      { title: "Profile & cover images — Quibblies" },
      {
        name: "description",
        content:
          "Upload your Quibblies profile picture and cover image. Every image is reviewed before it appears publicly.",
      },
      { property: "og:title", content: "Profile & cover images — Quibblies" },
      { property: "og:description", content: "Upload images for review before they go live." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: ProfileImagesPage,
});

const KINDS = [
  { key: "avatar" as const, label: "Profile picture", hint: "Square works best. Max 5MB." },
  { key: "banner" as const, label: "Cover image", hint: "Wide image, at least 1500px. Max 5MB." },
];

function ProfileImagesPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const load = useServerFn(listMyProfileImages);
  const submit = useServerFn(submitProfileImage);
  const [busy, setBusy] = React.useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["my-profile-images"],
    queryFn: () => load(),
    retry: false,
  });

  const record = useMutation({
    mutationFn: (input: { kind: "avatar" | "banner"; path: string }) => submit({ data: input }),
    onSuccess: () => {
      toast.success("Sent for review", {
        description: "An Administrator will approve it before it appears on your profile.",
      });
      void queryClient.invalidateQueries({ queryKey: ["my-profile-images"] });
    },
    onError: (e: Error) => toast.error("Could not submit", { description: e.message }),
  });

  async function handleFile(kind: "avatar" | "banner", file: File | undefined) {
    if (!file || !user) return;
    if (!file.type.startsWith("image/")) {
      toast.error("That file is not an image");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Images must be 5MB or smaller");
      return;
    }
    setBusy(kind);
    const extension = file.name.split(".").pop()?.toLowerCase() ?? "jpg";
    const path = `${user.id}/${kind}-${Date.now()}.${extension}`;
    const { error } = await supabase.storage.from("profile-images").upload(path, file, {
      cacheControl: "3600",
      upsert: false,
    });
    setBusy(null);
    if (error) {
      toast.error("Upload failed", { description: error.message });
      return;
    }
    record.mutate({ kind, path });
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <SectionHeading
        title="Profile & cover images"
        description="Images are private until an Administrator approves them. Your current image stays in place while a new one is reviewed."
      />

      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        {KINDS.map((kind) => {
          const latest = data?.find((row) => row.kind === kind.key);
          const status = latest?.status ?? null;
          return (
            <div key={kind.key} className="rounded-2xl border border-border/60 bg-surface/50 p-5">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-display text-lg text-foreground">{kind.label}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{kind.hint}</p>
                </div>
                <Badge
                  variant={
                    status === "rejected"
                      ? "destructive"
                      : status === "approved"
                        ? "default"
                        : "outline"
                  }
                >
                  {status === "pending"
                    ? "Awaiting review"
                    : status === "approved"
                      ? "Approved & live"
                      : status === "rejected"
                        ? "Rejected"
                        : "No upload yet"}
                </Badge>
              </div>
              {latest ? (
                <p className="mt-2 text-xs text-muted-foreground">
                  Submitted {new Date(latest.created_at).toLocaleString()}
                  {latest.reviewed_at
                    ? ` · reviewed ${new Date(latest.reviewed_at).toLocaleString()}`
                    : " · an Administrator will review it shortly"}
                </p>
              ) : null}
              {latest?.reason ? (
                <p className="mt-2 rounded-lg border border-border/60 bg-background/50 p-2 text-xs text-muted-foreground">
                  Reviewer note: {latest.reason}
                </p>
              ) : null}
              <label className="mt-4 flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-border/70 px-4 py-6 text-sm text-muted-foreground hover:border-primary/60">
                <UploadCloud className="h-4 w-4" aria-hidden="true" />
                {busy === kind.key ? "Uploading…" : "Choose an image"}
                <input
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  onChange={(e) => void handleFile(kind.key, e.target.files?.[0])}
                />
              </label>
            </div>
          );
        })}
      </div>


      <h2 className="mt-10 font-display text-xl text-foreground">Review history</h2>
      {isLoading ? (
        <Skeleton className="mt-4 h-32 w-full rounded-2xl" />
      ) : !data?.length ? (
        <p className="mt-3 text-sm text-muted-foreground">
          You have not submitted any images yet.
        </p>
      ) : (
        <ul className="mt-4 space-y-3">
          {data.map((row) => (
            <li
              key={row.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border/60 bg-surface/40 px-4 py-3 text-sm"
            >
              <span className="text-foreground">
                {row.kind === "avatar" ? "Profile picture" : "Cover image"} ·{" "}
                {new Date(row.created_at).toLocaleDateString()}
              </span>
              <span className="flex items-center gap-2">
                {row.reason ? (
                  <span className="text-xs text-muted-foreground">{row.reason}</span>
                ) : null}
                <Badge variant={row.status === "rejected" ? "destructive" : "outline"}>
                  {row.status}
                </Badge>
              </span>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-8 rounded-xl border border-border/60 bg-surface/40 p-4 text-xs text-muted-foreground">
        <strong className="text-foreground">Proof of age.</strong> Quibblies is strictly 18+. If we
        have reason to doubt an account holder&rsquo;s age, we may ask you to upload a photo of a
        government-issued ID. Those images are visible to Administrators and the Owner only — never
        to moderators, other members or the public — are used solely to confirm your age, and are
        deleted once the check is complete.
      </p>
      <p className="mt-4 text-xs text-muted-foreground">
        If an image is rejected and you think that was a mistake, you can appeal — the rejection
        message includes the instructions.
      </p>
      <Button asChild variant="ghost" size="sm" className="mt-3">
        <a href="/help">Read the image guidelines</a>
      </Button>

      <AgeVerificationPanel />
    </div>
  );
}
